import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SendDialog = ({ open, onOpenChange, rendered, sender, getHtml }) => {
  const [recipients, setRecipients] = useState("");
  const [sending, setSending] = useState(false);

  const onSend = async () => {
    const list = recipients.split(",").map((r) => r.trim()).filter(Boolean);
    if (!list.length) {
      toast.error("Enter at least one recipient email");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/send-email`, {
        template_id: rendered.id,
        recipients: list,
        subject: rendered.subject,
        html: getHtml(),
      });
      const { sent, failed } = res.data;
      if (sent) toast.success(`Sent to ${sent} recipient${sent > 1 ? "s" : ""}${failed ? `, ${failed} failed` : ""}`);
      else toast.error("All sends failed — check backend logs");
      if (sent && !failed) {
        setRecipients("");
        onOpenChange(false);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-normal">
            Send <em className="text-accent">{rendered?.name}</em>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sends the email exactly as previewed, including your edits.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-secondary/50 px-4 py-3 text-xs space-y-1">
            <p><span className="text-muted-foreground">From:</span>{" "}
              <span data-testid="send-from-name" className="font-semibold">{sender?.from_name} &lt;{sender?.from_email}&gt;</span></p>
            <p><span className="text-muted-foreground">Reply-to:</span>{" "}
              <span data-testid="send-reply-to" className="font-semibold">{sender?.from_email}</span></p>
            <p><span className="text-muted-foreground">Subject:</span> {rendered?.subject}</p>
          </div>
          <div>
            <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">
              Recipients (comma-separated, max 10)
            </label>
            <input
              data-testid="send-recipients-input"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="client@example.com, agent@example.com"
              className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>
          <button
            data-testid="send-confirm-btn"
            onClick={onSend}
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
